/**
 * API Client Utility
 * Centralized API client with authentication and error handling
 */

const COMMERCE_SERVICE_BASE_URL =
  import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083';

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

/**
 * Get authorization headers
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  const authHeader = token
    ? token.startsWith('Bearer ') ? token : `Bearer ${token}`
    : undefined;

  return {
    'Content-Type': 'application/json',
    ...(authHeader && { Authorization: authHeader }),
  };
}

/**
 * Handle API response
 */
async function handleResponse<T>(response: Response): Promise<{ data: T }> {
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return { data: null as T };
  }

  const data = await response.json();
  return { data };
}

/**
 * API Client
 */
export const apiClient = {
  async get<T>(endpoint: string): Promise<{ data: T }> {
    const response = await fetch(`${COMMERCE_SERVICE_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse<T>(response);
  },

  async post<T>(endpoint: string, body?: any): Promise<{ data: T }> {
    const response = await fetch(`${COMMERCE_SERVICE_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(response);
  },

  async put<T>(endpoint: string, body?: any): Promise<{ data: T }> {
    const response = await fetch(`${COMMERCE_SERVICE_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(response);
  },

  async delete<T>(endpoint: string): Promise<{ data: T }> {
    const response = await fetch(`${COMMERCE_SERVICE_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse<T>(response);
  },

  async downloadBlob(endpoint: string): Promise<Blob> {
    const response = await fetch(`${COMMERCE_SERVICE_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.blob();
  },
};
